import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../api/axios';

interface AffiliateStats {
  total_players: number;
  total_ggr: number;
}

interface AffiliateTrackingPageProps {
  tenantId?: number;
}

const AffiliateTrackingPage: React.FC<AffiliateTrackingPageProps> = ({ tenantId }) => {
  const [affiliateId, setAffiliateId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | 'week' | 'month' | 'custom'>('week');
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Set default dates on component mount
  useEffect(() => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    setStartDate(startOfWeek);
    setEndDate(now);
  }, []);

  const handleDateRangeChange = (range: 'today' | 'yesterday' | 'week' | 'month' | 'custom') => {
    setDateRange(range);
    const now = new Date();
    
    switch (range) {
      case 'today':
        setStartDate(now);
        setEndDate(now);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case 'week':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        setStartDate(startOfWeek);
        setEndDate(now);
        break;
      case 'month':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(startOfMonth);
        setEndDate(now);
        break;
      case 'custom':
        // Keep current dates for custom selection
        break;
    }
  };

  const fetchAffiliateStats = async () => {
    if (!affiliateId.trim()) {
      setSnackbar({ open: true, message: 'Please enter an affiliate ID', severity: 'error' });
      return;
    }

    if (!startDate || !endDate) {
      setSnackbar({ open: true, message: 'Please select date range', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        affiliate_id: affiliateId.trim(),
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });
      
      if (tenantId) {
        params.append('tenant_id', tenantId.toString());
      }

      const response = await api.get(`/api/v1/admin/affiliate-stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data as AffiliateStats);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch affiliate stats';
      setSnackbar({ open: true, message, severity: 'error' });
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>Affiliate Tracking</Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Affiliate ID"
                value={affiliateId}
                onChange={(e) => setAffiliateId(e.target.value)}
                placeholder="Enter affiliate ID"
                sx={{ minWidth: 200 }}
              />
              
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value as any)}
                  label="Date Range"
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">Week to Date</MenuItem>
                  <MenuItem value="month">Month to Date</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
              
              {dateRange === 'custom' && (
                <>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    slotProps={{ textField: { sx: { minWidth: 150 } } }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    slotProps={{ textField: { sx: { minWidth: 150 } } }}
                  />
                </>
              )}
              
              <Button
                variant="contained"
                onClick={fetchAffiliateStats}
                disabled={loading || !affiliateId.trim()}
                sx={{ minWidth: 120, height: 56 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Get Stats'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {stats && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Card sx={{ minWidth: 300, flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Players
                </Typography>
                <Typography variant="h3" component="div">
                  {stats.total_players.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Players signed up with affiliate ID: {affiliateId}
                </Typography>
              </CardContent>
            </Card>
            
            <Card sx={{ minWidth: 300, flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total GGR
                </Typography>
                <Typography variant="h3" component="div" color="success.main">
                  {formatCurrency(stats.total_ggr)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Gross Gaming Revenue from affiliate players
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={4000} 
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AffiliateTrackingPage;
